import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import useCustomNavigate from './useCustomNavigate';

/**
 * Hook to handle mobile back gestures/buttons on Capacitor
 *
 * This hook provides back gesture/button support for mobile devices when running in Capacitor.
 * It integrates with the existing navigation system and uses the same logic as the
 * back button in the topbar component.
 *
 * Features:
 * - iOS: Detects edge swipe gestures from left side
 * - Android: Handles hardware back button
 * - Uses the same history depth checking as topbar back button (topbar.tsx lines 72-75)
 * - Integrates with useCustomNavigate hook for consistent navigation behavior
 * - Automatically handles listener registration and cleanup
 * - Graceful error handling and development logging
 *
 * How it works:
 * iOS: Detects swipe gestures from left edge using touch events
 * Android: Listens for hardware back button via Capacitor App plugin
 * Both: Check if navigation history is available using navigate.getHistoryDepth()
 * If history exists, navigates back using navigate(-1)
 * If no history, allows platform to handle the gesture naturally (e.g., app exit)
 *
 * Usage:
 * Simply call this hook at the top level of your App component:
 * ```tsx
 * function App() {
 *   useMobileBackGesture();
 *   return <div>...</div>;
 * }
 * ```
 *
 * @requires @capacitor/core
 * @requires @capacitor/app
 * @requires useCustomNavigate hook
 */

const isProduction = process.env.NODE_ENV === 'production';

const useIOSBackGesture = () => {
    const navigate = useCustomNavigate();

    useEffect(() => {
        // Only activate on iOS Capacitor platform
        if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') {
            return;
        }

        // Touch event variables
        let startX = 0;
        let startY = 0;
        let startTime = 0;
        let isEdgeSwipe = false;

        const handleTouchStart = (e: TouchEvent) => {
            const touch = e.touches[0];
            startX = touch.clientX;
            startY = touch.clientY;
            startTime = Date.now();

            // Check if the swipe started from the left edge (within 50px from edge)
            isEdgeSwipe = startX <= 50;

            if (!isProduction && isEdgeSwipe) {
                console.log('iOS edge swipe detected - start:', { startX, startY });
            }
        };

        const handleTouchEnd = (e: TouchEvent) => {
            if (!isEdgeSwipe) return;

            const touch = e.changedTouches[0];
            const endX = touch.clientX;
            const endY = touch.clientY;
            const endTime = Date.now();

            const deltaX = endX - startX;
            const deltaY = endY - startY;
            const deltaTime = endTime - startTime;

            // Swipe gesture criteria:
            // - Started from left edge
            // - Moved right (deltaX > 0)
            // - Horizontal movement is greater than vertical (more horizontal than vertical)
            // - Minimum distance moved (at least 100px)
            // - Maximum time (within 800ms)
            // - Not too much vertical movement (less than 100px)
            const isValidSwipe =
                deltaX > 100 &&                    // Minimum horizontal distance
                Math.abs(deltaY) < 100 &&          // Maximum vertical movement
                deltaTime < 800 &&                 // Maximum time
                Math.abs(deltaX) > Math.abs(deltaY); // More horizontal than vertical

            if (!isProduction) {
                console.log('iOS swipe end:', {
                    deltaX,
                    deltaY,
                    deltaTime,
                    isValidSwipe,
                    isEdgeSwipe
                });
            }

            if (isValidSwipe) {
                handleBackGesture();
            }

            // Reset
            isEdgeSwipe = false;
        };

        const handleBackGesture = () => {
            try {
                // Use the same history depth check as the topbar component (line 73-75)
                const historyDepth = navigate.getHistoryDepth();

                // Only handle the gesture if we have navigation history
                if (historyDepth > 0) {
                    // Navigate back using the same logic as topbar back button (line 191)
                    navigate(-1);

                    // Log for debugging (can be removed in production)
                    if (!isProduction) {
                        console.log('iOS back gesture: navigated back, history depth:', historyDepth);
                    }
                } else {
                    // If no history available, let iOS handle the gesture naturally
                    // This allows the app to exit or perform other default behaviors
                    if (!isProduction) {
                        console.log('iOS back gesture: no history available, allowing default behavior');
                    }
                }
            } catch (error) {
                console.error('Error handling iOS back gesture:', error);
            }
        };

        // Add touch event listeners to document
        document.addEventListener('touchstart', handleTouchStart, { passive: true });
        document.addEventListener('touchend', handleTouchEnd, { passive: true });

        if (!isProduction) {
            console.log('iOS swipe gesture listeners registered for platform:', Capacitor.getPlatform());
        }

        // Cleanup listeners on unmount
        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchend', handleTouchEnd);

            if (!isProduction) {
                console.log('iOS swipe gesture listeners removed');
            }
        };
    }, [navigate]);
};

export default useIOSBackGesture;